

import React, { useState, useMemo, useEffect } from 'react';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  ExpandMore,
  Refresh,
  FilterList,
  Clear,
  Schedule,
  Timeline,
  BugReport,
  Close,
  Info
} from '@mui/icons-material';
import { format, parseISO, differenceInDays } from 'date-fns';
import { formatDate } from 'utils/DateTimeConversion';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';

  const statusColors = {
    success: 'success',
    failed: 'error',
    warning: 'warning',
    pending: 'info',
    partial_success: 'warning'
  };

  const statusIcons = {
    success: <CheckCircle />,
    failed: <Error />,
    warning: <Warning />,
    pending: <Schedule />,
    partial_success: <Warning />
  };

// A flow run (flow builder) logs the steps it took: {step_id, kind, type,
// outcome: done|failed|yes|no|waiting, preview, result, error}. These turn
// them into the {action, success, result, error} entries this screen reads;
// waits and conditions are shown as steps and not counted as actions. Logs of
// classic automations have no step_id and pass through unchanged.
const readableDetails = (details) =>
  (details || []).map((entry) => {
    if (!entry || !entry.step_id) return entry;
    if (entry.kind === 'action') {
      return { action: entry.type, success: entry.outcome !== 'failed', result: entry.result, error: entry.error };
    }
    const label = entry.kind === 'condition' ? `Condition: ${entry.outcome === 'yes' ? 'Yes' : 'No'} branch` : entry.preview || 'Wait';
    return { action: label, success: true, result: { message: entry.preview || label }, isStep: true };
  });

const actionEntries = (details) => readableDetails(details).filter((entry) => entry && !entry.isStep);

function AutomationLogs({ automation, onBack }) {
  const { axiosInstance } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [automationMetrics, setAutomationMetrics] = useState(null);
  const [scheduledActions, setScheduledActions] = useState([]);

  // Fetch automation logs
  const fetchLogs = async () => {
    if (!automation?.id) return;

    try {
      setLoading(true);
      setError(null);
      const [logsResponse, metricsResponse, scheduledResponse] = await Promise.all([
        axiosInstance.get(`/automations/${automation.id}/logs`),
        axiosInstance.get(`/automations/analytics/automation/${automation.id}/performance`),
        axiosInstance.get('/automations/scheduled-actions/debug')
      ]);
      
      console.log('AutomationLogs: Received logs from backend:', logsResponse.data);
      console.log('AutomationLogs: Number of logs:', logsResponse.data.length);
      if (logsResponse.data.length > 0) {
        console.log('AutomationLogs: First log structure:', logsResponse.data[0]);
        console.log('AutomationLogs: First log trigger fields:', {
          trigger_object: logsResponse.data[0].trigger_object,
          trigger_object_id: logsResponse.data[0].trigger_object_id,
          trigger_event: logsResponse.data[0].trigger_event,
          triggerObject: logsResponse.data[0].triggerObject,
          triggerObjectId: logsResponse.data[0].triggerObjectId,
          triggerEvent: logsResponse.data[0].triggerEvent,
          object: logsResponse.data[0].object,
          objectId: logsResponse.data[0].objectId,
          event: logsResponse.data[0].event
        });
        console.log('AutomationLogs: All available fields:', Object.keys(logsResponse.data[0]));
      }
      
      setLogs(logsResponse.data);
      setAutomationMetrics(metricsResponse.data);
      setScheduledActions(scheduledResponse.data.pending_actions_details || []);
    } catch (err) {
      console.error('Error fetching automation logs:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(`Failed to load automation logs: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [automation?.id]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleViewDetails = (log) => {
    // console.log('AutomationLogs: Viewing log details:', log);
    // console.log('AutomationLogs: Log trigger fields:', {
    //   trigger_object: log.trigger_object,
    //   trigger_object_id: log.trigger_object_id,
    //   trigger_event: log.trigger_event,
    //   triggerObject: log.triggerObject,
    //   triggerObjectId: log.triggerObjectId,
    //   triggerEvent: log.triggerEvent,
    //   object: log.object,
    //   objectId: log.objectId,
    //   event: log.event
    // });
    setSelectedLog(log);
    setDetailDialog(true);
  };

  const clearFilters = () => {
    setFilterStatus('');
    setFilterDate('');
    setSearchTerm('');
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  const handleProcessScheduledActions = async () => {
    try {
      const response = await axiosInstance.post('/automations/scheduled-actions/process-immediate');
      console.log('Scheduled actions processed:', response.data);
      // Refresh logs to show updated status
      fetchLogs();
    } catch (err) {
      console.error('Error processing scheduled actions:', err);
    }
  };

  const filteredAndSortedLogs = useMemo(() => {
    let filtered = logs.filter(log => {
      const matchesSearch =
        ((log.trigger_object || log.triggerObject || log.object || '') + '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (log.error_message || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        ((log.trigger_event || log.triggerEvent || log.event || '') + '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !filterStatus || getActualExecutionStatus(log) === filterStatus;

      let matchesDate = true;
      if (filterDate && log.created_at) {
        const logDate = parseISO(log.created_at);
        const today = new Date();
        switch (filterDate) {
          case 'today':
            matchesDate = differenceInDays(today, logDate) === 0;
            break;
          case 'week':
            matchesDate = differenceInDays(today, logDate) <= 7;
            break;
          case 'month':
            matchesDate = differenceInDays(today, logDate) <= 30;
            break;
          default:
            matchesDate = true;
        }
      }

      return matchesSearch && matchesStatus && matchesDate;
    });

    filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [logs, orderBy, order, filterStatus, filterDate, searchTerm]);

  const getStatusStats = () => {
    const stats = logs.reduce((acc, log) => {
      const actualStatus = getActualExecutionStatus(log);
      acc[actualStatus] = (acc[actualStatus] || 0) + 1;
      return acc;
    }, {});
    return stats;
  };

  const getAverageExecutionTime = () => {
    const executionTimes = logs
      .filter(log => log.execution_time)
      .map(log => parseFloat(log.execution_time));

    if (executionTimes.length === 0) return '0ms';

    const average = executionTimes.reduce((sum, time) => sum + time, 0) / executionTimes.length;
    return `${average.toFixed(2)}ms`;
  };

  // Function to determine actual execution status based on actions
  const getActualExecutionStatus = (log) => {
    // If there are execution details with actions, check their success
    if (log.execution_details && Array.isArray(log.execution_details)) {
      const ran = actionEntries(log.execution_details);
      const totalActions = ran.length;
      const successfulActions = ran.filter(action => action.success).length;
      const failedActions = ran.filter(action => !action.success).length;

      if (failedActions > 0 && successfulActions === 0) {
        return 'failed';
      } else if (failedActions > 0 && successfulActions > 0) {
        return 'partial_success';
      } else if (successfulActions > 0 && failedActions === 0) {
        return 'success';
      }
    }
    
    // Fallback to original status
    return log.execution_status;
  };

  const renderStatsCards = () => {
    const stats = getStatusStats();
    const successRate = logs.length > 0 ? (((stats.success || 0) + (stats.partial_success || 0)) / logs.length * 100).toFixed(1) : 0;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={1.7}>
          <Card sx={{
            p: 2,
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)' },
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <MDTypography variant="h6" fontWeight="bold" color="info" sx={{ mb: 0.5, fontSize: '1.25rem' }}>
              {logs.length}
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
              Total Executions
            </MDTypography>
          </Card>
        </Grid>
        <Grid item xs={12} md={1.7}>
          <Card sx={{
            p: 2,
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)' },
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <MDTypography variant="h6" fontWeight="bold" color="success" sx={{ mb: 0.5, fontSize: '1.25rem' }}>
              {stats.success || 0}
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
              Successful
            </MDTypography>
          </Card>
        </Grid>
        <Grid item xs={12} md={1.7}>
          <Card sx={{
            p: 2,
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)' },
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <MDTypography variant="h6" fontWeight="bold" color="error" sx={{ mb: 0.5, fontSize: '1.25rem' }}>
              {stats.failed || 0}
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
              Failed
            </MDTypography>
          </Card>
        </Grid>
        <Grid item xs={12} md={1.7}>
          <Card sx={{
            p: 2,
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)' },
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <MDTypography variant="h6" fontWeight="bold" color="warning" sx={{ mb: 0.5, fontSize: '1.25rem' }}>
              {stats.warning || 0}
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
              Warnings
            </MDTypography>
          </Card>
        </Grid>
        <Grid item xs={12} md={1.7}>
          <Card sx={{
            p: 2,
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)' },
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <MDTypography variant="h6" fontWeight="bold" color="warning" sx={{ mb: 0.5, fontSize: '1.25rem' }}>
              {stats.partial_success || 0}
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
              Partial Success
            </MDTypography>
          </Card>
        </Grid>
        <Grid item xs={12} md={1.7}>
          <Card sx={{
            p: 2,
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)' },
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <MDTypography variant="h6" fontWeight="bold" color="info" sx={{ mb: 0.5, fontSize: '1.25rem' }}>
              {successRate}%
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
              Success Rate
            </MDTypography>
          </Card>
        </Grid>
        <Grid item xs={12} md={1.7}>
          <Card sx={{
            p: 2,
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.15)' },
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <MDTypography variant="h6" fontWeight="bold" color="secondary" sx={{ mb: 0.5, fontSize: '1.25rem' }}>
              {getAverageExecutionTime()}
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" fontWeight="medium" sx={{ fontSize: '0.75rem' }}>
              Avg. Time
            </MDTypography>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // const renderAutomationMetrics = () => {
  //   if (!automationMetrics) return null;

  //   return (
  //     <Card sx={{ mb: 3, p: 2.5, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
  //       <MDTypography variant="h6" fontWeight="bold" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
  //         📊 Automation Performance Metrics
  //       </MDTypography>
  //       <Grid container spacing={3}>
  //         <Grid item xs={12} md={3}>
  //           <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
  //             <MDTypography variant="h5" fontWeight="bold" color="primary">
  //               {automationMetrics.total_executions || 0}
  //             </MDTypography>
  //             <MDTypography variant="body2" color="text.secondary">
  //               Total Executions (30 days)
  //             </MDTypography>
  //           </Box>
  //         </Grid>
  //         <Grid item xs={12} md={3}>
  //           <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
  //             <MDTypography variant="h5" fontWeight="bold" color="success.main">
  //               {automationMetrics.success_rate || 0}%
  //             </MDTypography>
  //             <MDTypography variant="body2" color="text.secondary">
  //               Success Rate
  //             </MDTypography>
  //           </Box>
  //         </Grid>
  //         <Grid item xs={12} md={3}>
  //           <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
  //             <MDTypography variant="h5" fontWeight="bold" color="info.main">
  //               {automationMetrics.avg_execution_time || 0}ms
  //             </MDTypography>
  //             <MDTypography variant="body2" color="text.secondary">
  //               Avg Execution Time
  //             </MDTypography>
  //           </Box>
  //         </Grid>
  //         <Grid item xs={12} md={3}>
  //           <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
  //             <MDTypography variant="h5" fontWeight="bold" color="warning.main">
  //               {automationMetrics.failure_rate || 0}%
  //             </MDTypography>
  //             <MDTypography variant="body2" color="text.secondary">
  //               Failure Rate
  //             </MDTypography>
  //           </Box>
  //         </Grid>
  //       </Grid>
  //     </Card>
  //   );
  // };

  const renderScheduledActions = () => {
    if (!scheduledActions || scheduledActions.length === 0) return null;

    const relevantActions = scheduledActions.filter(action => 
      action.automation_id === automation?.id
    );

    if (relevantActions.length === 0) return null;

    return (
      <Card sx={{ mb: 3, p: 2.5, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <MDTypography variant="h6" fontWeight="bold" sx={{ color: 'warning.main' }}>
            ⏰ Scheduled Actions ({relevantActions.length})
          </MDTypography>
          {/* <MDButton
            variant="outlined"
            size="small"
            startIcon={<Schedule />}
            onClick={handleProcessScheduledActions}
            sx={{ color: 'warning.main', borderColor: 'warning.main' }}
          >
            Process Now
          </MDButton> */}
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {relevantActions.map((action, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                backgroundColor: action.is_ready ? '#fff3cd' : '#f8f9fa',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {/* Left side - Text content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip
                    label={action.action_type}
                    size="small"
                    color="info"
                    sx={{ 
                      fontSize: '0.7rem',
                      fontWeight: 'medium',
                      height: '24px'
                    }}
                  />
                  <Chip
                    label={action.is_ready ? 'Ready' : 'Pending'}
                    size="small"
                    color={action.is_ready ? 'warning' : 'default'}
                    sx={{ 
                      fontSize: '0.7rem',
                      fontWeight: 'medium',
                      height: '24px'
                    }}
                  />
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.8rem', 
                    color: 'text.secondary',
                    fontWeight: 'medium',
                    mb: 0.5
                  }}
                >
                  Scheduled for: {action.scheduled_at ? new Date(action.scheduled_at).toLocaleString() : 'Unknown'}
                </Typography>
                {action.time_delay_config && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.7rem', 
                      color: 'text.secondary',
                      fontStyle: 'italic'
                    }}
                  >
                    Delay: {action.time_delay_config.days}d {action.time_delay_config.hours}h {action.time_delay_config.minutes}m
                  </Typography>
                )}
              </Box>

              {/* Right side - Status indicator */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: action.is_ready ? '#ffc107' : '#6c757d',
                    animation: action.is_ready ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 }
                    }
                  }}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.7rem',
                    fontWeight: 'medium',
                    color: action.is_ready ? 'warning.main' : 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  {action.is_ready ? 'Ready' : 'Waiting'}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
        
        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            These actions are scheduled to run automatically.
          </Typography>
        </Alert>
      </Card>
    );
  };

  const renderFilters = () => (
    <Card sx={{ mb: 3, p: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Search logs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by record name or message..."
            size="small"
            sx={{
              height: '40px',
              borderRadius: '8px',
              '& .MuiSelect-select': {
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 14px'
              },
              '& .MuiOutlinedInput-root': {
                height: '40px',
                '&:hover fieldset': {
                  borderColor: 'primary.main'
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small" >
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Filter by Status"
              sx={{
                height: '40px',
                borderRadius: '8px',
                '& .MuiSelect-select': {
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 14px'
                },
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="partial_success">Partial Success</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Date</InputLabel>
            <Select
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              label="Filter by Date"
              sx={{
                height: '40px',
                borderRadius: '8px',
                '& .MuiSelect-select': {
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 14px'
                },
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            >
              <MenuItem value="">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">Past Week</MenuItem>
              <MenuItem value="month">Past Month</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Box display="flex" gap={1}>
            <MDButton
              variant="outlined"
              size="small"
              startIcon={<Clear />}
              onClick={clearFilters}
            >
              Clear
            </MDButton>
            <MDButton
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={handleRefresh}
            >
              Refresh
            </MDButton>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );

  const renderEmptyState = () => (
    <Paper sx={{ p: 6, textAlign: 'center' }}>
      <Timeline sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        {loading ? 'Loading logs...' : 'No logs found'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {loading ? 'Please wait while we fetch the automation logs.' :
          searchTerm || filterStatus || filterDate ?
            'No logs match your current filters. Try adjusting your search criteria.' :
            'This automation hasn\'t been executed yet or logs are not available.'
        }
      </Typography>
      {(searchTerm || filterStatus || filterDate) && !loading && (
        <MDButton variant="outlined" onClick={clearFilters}>
          Clear Filters
        </MDButton>
      )}
    </Paper>
  );

  const renderLogDetails = () => (
    <Dialog
      open={detailDialog}
      onClose={() => setDetailDialog(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: 'primary.main',
        color: 'white',
        py: 2.5,
        px: 3
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            {selectedLog && (
              <Box sx={{
                backgroundColor: 'rgb(255, 255, 255)',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'info.main'
              }}>
                {statusIcons[getActualExecutionStatus(selectedLog)]}
              </Box>
            )}
            <MDTypography variant="h5" fontWeight="bold" color="white">
              Execution Details
            </MDTypography>
          </Box>
          <IconButton
            onClick={() => setDetailDialog(false)}
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: '#fafafa' }}>
        {selectedLog && (
          <Box>
            {/* {console.log('AutomationLogs: Dialog selectedLog:', selectedLog)}
            {console.log('AutomationLogs: Dialog trigger fields:', {
              trigger_object: selectedLog.trigger_object,
              trigger_object_id: selectedLog.trigger_object_id,
              trigger_event: selectedLog.trigger_event,
              triggerObject: selectedLog.triggerObject,
              triggerObjectId: selectedLog.triggerObjectId,
              triggerEvent: selectedLog.triggerEvent,
              object: selectedLog.object,
              objectId: selectedLog.objectId,
              event: selectedLog.event
            })} */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  border: '1px solid #e0e0e0',
                  '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }
                }}>
                  <MDTypography variant="h6" fontWeight="bold" gutterBottom sx={{
                    color: 'primary.main',
                    borderBottom: '2px solid',
                    borderColor: 'primary.light',
                    pb: 0.5,
                    mb: 1.5
                  }}>
                    Basic Information
                  </MDTypography>
                  <List dense sx={{ '& .MuiListItem-root': { px: 0 } }}>
                    <ListItem sx={{
                      py: 0.5,
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <ListItemText
                        primary={
                          <MDTypography variant="body2" fontWeight="medium" color="text.primary" sx={{ mb: 0.25, fontSize: '0.85rem' }}>
                            Status
                          </MDTypography>
                        }
                        secondary={
                                                  <Chip
                          icon={statusIcons[getActualExecutionStatus(selectedLog)]}
                          label={getActualExecutionStatus(selectedLog)}
                          color={statusColors[getActualExecutionStatus(selectedLog)]}
                          size="small"
                          sx={{
                            color: '#ffffff',
                            height: '20px',
                            fontWeight: 'medium',
                            fontSize: '0.7rem',
                            '& .MuiChip-label': {
                              color: '#ffffff'
                            },
                            '& .MuiChip-icon': {
                              color: '#ffffff',
                              fontSize: '0.9rem'
                            }
                          }}
                        />
                        }
                      />
                    </ListItem>
                    <ListItem sx={{
                      py: 0.5,
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <ListItemText
                        primary={
                          <MDTypography variant="body2" fontWeight="medium" color="text.primary" sx={{ mb: 0.25, fontSize: '0.85rem' }}>
                            Automation ID
                          </MDTypography>
                        }
                        secondary={
                          <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {selectedLog.automation_id}
                          </MDTypography>
                        }
                      />
                    </ListItem>
                    <ListItem sx={{
                      py: 0.5,
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <ListItemText
                        primary={
                          <MDTypography variant="body2" fontWeight="medium" color="text.primary" sx={{ mb: 0.25, fontSize: '0.85rem' }}>
                            Created Date
                          </MDTypography>
                        }
                        secondary={
                          <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {formatDate(selectedLog.created_at, 'datetime')}
                          </MDTypography>
                        }
                      />
                    </ListItem>
                    <ListItem sx={{
                      py: 0.5,
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <ListItemText
                        primary={
                          <MDTypography variant="body2" fontWeight="medium" color="text.primary" sx={{ mb: 0.25, fontSize: '0.85rem' }}>
                            Trigger Object
                          </MDTypography>
                        }
                        secondary={
                          <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {selectedLog.trigger_object || selectedLog.triggerObject || selectedLog.object || 'Not specified'}
                          </MDTypography>
                        }
                      />
                    </ListItem>
                    <ListItem sx={{
                      py: 0.5,
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <ListItemText
                        primary={
                          <MDTypography variant="body2" fontWeight="medium" color="text.primary" sx={{ mb: 0.25, fontSize: '0.85rem' }}>
                            Trigger Object ID
                          </MDTypography>
                        }
                        secondary={
                          <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {selectedLog.trigger_object_id || selectedLog.triggerObjectId || selectedLog.objectId || 'Not specified'}
                          </MDTypography>
                        }
                      />
                    </ListItem>
                    <ListItem sx={{
                      py: 0.5,
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <ListItemText
                        primary={
                          <MDTypography variant="body2" fontWeight="medium" color="text.primary" sx={{ mb: 0.25, fontSize: '0.85rem' }}>
                            Trigger Event
                          </MDTypography>
                        }
                        secondary={
                          <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {selectedLog.trigger_event || selectedLog.triggerEvent || selectedLog.event || 'Not specified'}
                          </MDTypography>
                        }
                      />
                    </ListItem>
                    {(!selectedLog.trigger_object && !selectedLog.trigger_event) && (
                      <ListItem sx={{
                        py: 0.5,
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <ListItemText
                          primary={
                            <MDTypography variant="body2" fontWeight="medium" color="text.primary" sx={{ mb: 0.25, fontSize: '0.85rem' }}>
                              Note
                            </MDTypography>
                          }
                          secondary={
                            <MDTypography variant="body2" color="text.secondary" sx={{
                              fontSize: '0.8rem',
                              fontStyle: 'italic',
                              color: 'warning.main'
                            }}>
                              This automation was executed manually or through a scheduled trigger
                            </MDTypography>
                          }
                        />
                      </ListItem>
                    )}
                    <ListItem sx={{
                      py: 0.5,
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <ListItemText
                        primary={
                          <MDTypography variant="body2" fontWeight="medium" color="text.primary" sx={{ mb: 0.25, fontSize: '0.85rem' }}>
                            Execution Time
                          </MDTypography>
                        }
                        secondary={
                          <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {selectedLog.execution_time ? 
                              (typeof selectedLog.execution_time === 'number' ? 
                                `${selectedLog.execution_time}ms` : 
                                (selectedLog.execution_time && !isNaN(parseFloat(selectedLog.execution_time)) ? 
                                  `${parseFloat(selectedLog.execution_time).toFixed(2)}ms` : 
                                  ' '
                                )
                              ) : ' '
                            }
                          </MDTypography>
                        }
                      />
                    </ListItem>
                  </List>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  border: '1px solid #e0e0e0',
                  '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }
                }}>
                  <MDTypography variant="h6" fontWeight="bold" gutterBottom sx={{
                    color: 'primary.main',
                    borderBottom: '2px solid',
                    borderColor: 'primary.light',
                    pb: 0.5,
                    mb: 1.5
                  }}>
                    Message
                  </MDTypography>
                  <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.5, fontSize: '0.9rem' }}>
                    {selectedLog.error_message ||
                      (selectedLog.execution_details && Array.isArray(selectedLog.execution_details) && selectedLog.execution_details.length > 0
                        ? `Successfully executed ${actionEntries(selectedLog.execution_details).length} action(s)`
                        : 'Automation executed successfully')}
                  </Typography>

                  {selectedLog.execution_status === 'failed' && selectedLog.error_message && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        <strong>Error:</strong> {selectedLog.error_message}
                      </Typography>
                    </Alert>
                  )}

                  {/* Execution Details moved here */}
                  {selectedLog.execution_details && Array.isArray(selectedLog.execution_details) && (
                    <Box sx={{ mt: 2 }}>
                      <MDTypography variant="subtitle2" fontWeight="bold" sx={{
                        color: 'primary.main',
                        mb: 1,
                        fontSize: '0.9rem'
                      }}>
                        Actions Executed:
                      </MDTypography>
                      <List dense sx={{
                        '& .MuiListItem-root': {
                          px: 0,
                          borderRadius: 1,
                          mb: 1,
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #e9ecef',
                          padding: '12px 16px'
                        }
                      }}>
                        {readableDetails(selectedLog.execution_details).map((action, index) => (
                          <ListItem key={index} sx={{ py: 1, px: 0 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1.5,
                                  mb: 1
                                }}>
                                  <Chip
                                    icon={action.success ? <CheckCircle sx={{ fontSize: '0.8rem' }} /> : <Error sx={{ fontSize: '0.8rem' }} />}
                                    label={`${action.action} - ${action.success ? 'Success' : 'Failed'}`}
                                    color={action.success ? 'success' : 'error'}
                                    size="small"
                                    sx={{
                                      color: '#ffffff',
                                      fontSize: '0.7rem',
                                      fontWeight: 'medium',
                                      height: '24px',
                                      '& .MuiChip-label': {
                                        padding: '0 8px'
                                      }
                                    }}
                                  />
                                </Box>
                              }
                              secondary={
                                <Typography variant="body2" sx={{
                                  mt: 0.5,
                                  color: 'text.secondary',
                                  fontSize: '0.8rem',
                                  lineHeight: 1.4,
                                  fontWeight: 'medium'
                                }}>
                                  {action.success
                                    ? (action.result?.message ||
                                      (action.result?.object_type && action.result?.record_id
                                        ? `Created ${action.result.object_type} with ID: ${action.result.record_id}`
                                        : 'Action completed successfully'))
                                    : action.error || 'Action failed'
                                  }
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {selectedLog.execution_details && !Array.isArray(selectedLog.execution_details) && (
                    <Box sx={{ mt: 2 }}>
                      <MDTypography variant="subtitle2" fontWeight="bold" sx={{
                        color: 'primary.main',
                        mb: 1,
                        fontSize: '0.9rem'
                      }}>
                        Execution Details:
                      </MDTypography>
                      <Box
                        className="font-mono"
                        data-font="mono"
                        sx={{
                        fontSize: '0.7rem',
                        bgcolor: '#f8f9fa',
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid #e9ecef',
                        maxHeight: '200px',
                        overflow: 'auto'
                      }}>
                        {JSON.stringify(selectedLog.execution_details, null, 2)}
                      </Box>
                    </Box>
                  )}

                  {(!selectedLog.execution_details || (Array.isArray(selectedLog.execution_details) && selectedLog.execution_details.length === 0)) && (
                    <Box sx={{ mt: 2 }}>
                      <MDTypography variant="subtitle2" fontWeight="bold" sx={{
                        color: 'primary.main',
                        mb: 1,
                        fontSize: '0.9rem'
                      }}>
                        Raw Log Data:
                      </MDTypography>
                      <Box
                        className="font-mono"
                        data-font="mono"
                        sx={{
                        fontSize: '0.7rem',
                        bgcolor: '#f8f9fa',
                        p: 1.5,
                        borderRadius: 1.5,
                        border: '1px solid #e9ecef',
                        maxHeight: '200px',
                        overflow: 'auto'
                      }}>
                        {JSON.stringify(selectedLog, null, 2)}
                      </Box>
                    </Box>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        backgroundColor: '#fafafa',
        borderTop: '1px solid #e0e0e0'
      }}>
        <MDButton
          onClick={() => setDetailDialog(false)}
          color="secondary"
          variant="contained"
          sx={{ color: 'white !important' }}
        >
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );

  return (
    <MDBox>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : (
        <>
          {renderStatsCards()}
          {/* {renderAutomationMetrics()} */}
          {renderScheduledActions()}
          {renderFilters()}

          {!loading && filteredAndSortedLogs.length === 0 ? (
            renderEmptyState()
          ) : !loading && filteredAndSortedLogs.length > 0 ? (
            <Card sx={{
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              <TableContainer>
                <Table sx={{ minWidth: 800 }}>
                  <TableBody>
                    {/* Header Row */}
                    <TableRow sx={{ backgroundColor: '#f8f9fa', '& > *': { borderBottom: '2px solid #e0e0e0' } }}>
                      <TableCell sx={{
                        width: '20%',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        padding: '12px 16px',
                        color: 'text.primary'
                      }}>
                        <TableSortLabel
                          active={orderBy === 'created_at'}
                          direction={orderBy === 'created_at' ? order : 'asc'}
                          onClick={() => handleSort('created_at')}
                          sx={{ fontSize: '0.8rem' }}
                        >
                          Timestamp
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{
                        width: '12%',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        padding: '12px 16px',
                        color: 'text.primary'
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{
                        width: '15%',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        padding: '12px 16px',
                        color: 'text.primary'
                      }}>
                        Record
                      </TableCell>
                      <TableCell sx={{
                        width: '25%',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        padding: '12px 16px',
                        color: 'text.primary'
                      }}>
                        Message
                      </TableCell>
                      <TableCell sx={{
                        width: '12%',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        padding: '12px 16px',
                        color: 'text.primary'
                      }}>
                        Execution Time
                      </TableCell>
                      <TableCell sx={{
                        width: '16%',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        padding: '12px 16px',
                        color: 'text.primary'
                      }} align="center">
                        Actions
                      </TableCell>
                    </TableRow>

                    {/* Data Rows */}
                    {filteredAndSortedLogs.map((log) => (
                      <TableRow key={log.id} hover sx={{ '& > *': { borderBottom: '1px solid #f0f0f0' } }}>
                        <TableCell sx={{
                          width: '20%',
                          padding: '12px 16px',
                          verticalAlign: 'middle'
                        }}>
                          <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {formatDate(log.created_at, 'datetime')}
                          </MDTypography>
                        </TableCell>
                        <TableCell sx={{
                          width: '12%',
                          padding: '12px 16px',
                          verticalAlign: 'middle'
                        }}>
                          <Chip
                            icon={statusIcons[getActualExecutionStatus(log)]}
                            label={getActualExecutionStatus(log)}
                            color={statusColors[getActualExecutionStatus(log)]}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 'medium',
                              color: '#ffffff',
                              '& .MuiChip-label': {
                                color: '#ffffff'
                              },
                              '& .MuiChip-icon': {
                                color: '#ffffff'
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{
                          width: '15%',
                          padding: '12px 16px',
                          verticalAlign: 'middle'
                        }}>
                          <MDTypography variant="body2" fontWeight="medium" color="text.primary" sx={{ fontSize: '0.75rem' }}>
                            {log.trigger_object || log.triggerObject || log.object || 'Not specified'}
                          </MDTypography>
                          <MDTypography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            ID: {log.trigger_object_id || log.triggerObjectId || log.objectId || 'Not specified'}
                          </MDTypography>
                        </TableCell>
                        <TableCell sx={{
                          width: '25%',
                          padding: '12px 16px',
                          verticalAlign: 'middle'
                        }}>
                          <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {log.error_message ||
                              (log.execution_details && Array.isArray(log.execution_details) && log.execution_details.length > 0
                                ? `${actionEntries(log.execution_details).length} action(s) executed`
                                : (log.trigger_event || log.triggerEvent || log.event || 'Automation executed successfully'))}
                          </MDTypography>
                        </TableCell>
                        <TableCell sx={{
                          width: '12%',
                          padding: '12px 16px',
                          verticalAlign: 'middle'
                        }}>
                          <MDTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {log.execution_time ? 
                              (typeof log.execution_time === 'number' ? 
                                `${log.execution_time}ms` : 
                                (log.execution_time && !isNaN(parseFloat(log.execution_time)) ? 
                                  `${parseFloat(log.execution_time).toFixed(2)}ms` : 
                                  ''
                                )
                              ) : ''
                            }
                          </MDTypography>
                        </TableCell>
                        <TableCell sx={{
                          width: '16%',
                          padding: '12px 16px',
                          verticalAlign: 'middle'
                        }} align="center">
                          <Box display="flex" gap={0.5} justifyContent="center">
                            <MDButton
                              variant="outlined"
                              size="small"
                              onClick={() => handleViewDetails(log)}
                              sx={{
                                fontSize: '0.7rem',
                                py: 0.5,
                                px: 1.5,
                                minWidth: 'auto',
                                textTransform: 'none',
                                fontWeight: 'medium',
                                color: 'info.main',
                                borderColor: 'info.main'
                              }}
                            >
                              View Details
                            </MDButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          ) : null}

          {renderLogDetails()}
        </>
      )}
    </MDBox>
  );
}

export default AutomationLogs; 